import { HttpService } from "../../modules/shared/services/http.service";
import { 
  UserNotification, 
  UserNotificationCreateDto, 
  UserNotificationUpdateDto,
  UserNotificationResponse,
  UserNotificationLatestResponse
} from "../../modules/shared/model";
import {
  CustomNotificationSettings,
  CustomNotificationSettingsCreateDto,
  CustomNotificationSettingsUpdateDto,
  CustomNotificationSettingsResponse,
  CustomNotificationSettingsLatestResponse
} from "../../modules/shared/model/custom-notification-settings.interfaces";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// Get all user notifications with pagination
export const getUserNotifications = async (query?: Record<string, string | number | boolean>): Promise<UserNotificationResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/notifications${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get user notifications by user
export const getUserNotificationsByUser = async (userId: string, query?: Record<string, string | number | boolean>): Promise<UserNotificationResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/notifications/user/${userId}${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get unread notification count for a user
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  const qs = new URLSearchParams();
  qs.append("limit", "1"); // Only need count, not data
  qs.append("filters", JSON.stringify({ status: "1" })); // Unread status
  
  const response = await HttpService.get(`api/v1/notifications/user/${userId}?${qs}`, true, { "x-custom-lang": "en" });
  return response.totalCount || 0;
};

// Get user notification by ID
export const getUserNotificationById = async (id: string): Promise<UserNotification> => {
  const response = await HttpService.get(`api/v1/notifications/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new user notification
export const createUserNotificationAction = async (data: UserNotificationCreateDto): Promise<UserNotification> => {
  const response = await HttpService.post("api/v1/notifications", data, headers, true);
  return response;
};

// Update user notification
export const updateUserNotificationAction = async (id: string, data: UserNotificationUpdateDto): Promise<UserNotification> => {
  const response = await HttpService.patch(`api/v1/notifications/${id}`, data, headers, true);
  return response;
};

// Delete user notification
export const deleteUserNotificationAction = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/notifications/${id}`, { "x-custom-lang": "en" }, true);
};

// Bulk create user notifications
export const bulkCreateUserNotificationsAction = async (notifications: UserNotificationCreateDto[]): Promise<UserNotification[]> => {
  const response = await HttpService.post("api/v1/notifications/bulk", { notifications }, headers, true);
  return response;
};

// Bulk update user notifications
export const bulkUpdateUserNotificationsAction = async (notifications: Array<{ id: string; data: UserNotificationUpdateDto }>): Promise<UserNotification[]> => {
  const response = await HttpService.patch("api/v1/notifications/bulk", { notifications }, headers, true);
  return response;
};

// ===== CUSTOM NOTIFICATION SETTINGS APIs =====

// Get latest custom notification settings
export const getLatestCustomNotificationSettings = async (): Promise<CustomNotificationSettingsLatestResponse> => {
  const response = await HttpService.get("api/v1/custom-notification-settings/latest", true, { "x-custom-lang": "en" });
  return response;
};

// Get all custom notification settings with pagination
export const getCustomNotificationSettings = async (query?: Record<string, string | number | boolean>): Promise<CustomNotificationSettingsResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/custom-notification-settings${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get custom notification settings by ID
export const getCustomNotificationSettingsById = async (id: string): Promise<CustomNotificationSettings> => {
  const response = await HttpService.get(`api/v1/custom-notification-settings/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new custom notification settings
export const createCustomNotificationSettings = async (data: CustomNotificationSettingsCreateDto): Promise<CustomNotificationSettings> => {
  const response = await HttpService.post("api/v1/custom-notification-settings", data, headers, true);
  return response;
};

// Update custom notification settings
export const updateCustomNotificationSettings = async (id: string, data: CustomNotificationSettingsUpdateDto): Promise<CustomNotificationSettings> => {
  const response = await HttpService.patch(`api/v1/custom-notification-settings/${id}`, data, headers, true);
  return response;
};

// Delete custom notification settings
export const deleteCustomNotificationSettings = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/custom-notification-settings/${id}`, { "x-custom-lang": "en" }, true);
};

